import { convertBlobToFile, generateFileName, onUploadProgress } from "../../helpers/Functions";
import authService from "../AuthService";
import adminApiService from "./AdminApiService";
import ApiService from "./ApiService";
import { convertToEnglishDigit } from "../../helpers/Functions";
import { baseURLV2 } from "../../axios";

class ProviderApiService extends ApiService {
  /**
   *
   * @param {number} providerID
   * @returns {Promise}
   */
  async menu(providerID) {
    try {
      const res = await this.axios.get(`providers/${providerID}/categories`);
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} providerId
   * @returns {Promise}
   */
  async getSettings(providerId) {
    try {
      const res = await this.axios.get(
        `providers/${providerId}/settings`, { baseURL: baseURLV2 }
      );
      return res.data;
    } catch (err) {
      return this.throwFetchError();
    }
  }

  /**
   *
   * @param {number} providerID
   * @returns {Promise}
   */
  async providerProfile(providerID) {
    try {
      const res = await this.axios.get(`providers/${providerID}`);
      return res.data;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} providerId
   * @param {string} name
   * @param {string} logo
   * @param {JSON} phones
   * @param {JSON} social_medias
   * @param {string} about
   * @param {string} hour
   * @param {string} slogan
   * @returns {Promise}
   */
  async storeOrEditProviderProfile({
    providerId,
    name,
    persian_name,
    english_name,
    logo,
    covers,
    phones,
    social_medias,
    about,
    slogan,
    updateProgress,
    abortController
  }) {
    const formData = new FormData();

    formData.append("name", name);
    formData.append("persian_name", persian_name);
    formData.append("english_name", english_name);

    if (logo instanceof Blob) {
      formData.append("logo[]", convertBlobToFile(logo));
    }

    if (covers) {
      covers.forEach(cover => {
        if (cover instanceof File) {
          formData.append("cover[]", cover);
        }
      });
    }

    formData.append(
      "phones",
      phones ? JSON.stringify(JSON.stringify(phones)) : null
    );
    formData.append(
      "social_medias",
      social_medias ? JSON.stringify(JSON.stringify(social_medias)) : null
    );
    formData.append("about", about ? about : "");
    if (slogan) formData.append("slogan", slogan);
    formData.append("_method", "PATCH");

    let url = `providers/${providerId}`;

    try {
      const res = await this.axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        signal: abortController.signal,
        onUploadProgress: (progressEvent) => {
          const result = onUploadProgress(progressEvent);
          updateProgress(result);
        },
      });
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} providerID
   * @returns {Promise}
   */
  async getCustomersClub(providerID) {
    try {
      const res = await this.axios.get(`providers/${providerID}/customers`);
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} providerID
   * @returns {Promise}
   */
  async getProviderPlans(providerID) {
    try {
      const res = await this.axios.get(`providers/${providerID}/plans`);
      return res.data;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} providerId
   * @returns {Promise}
   */
  async getProviderFinancialReports(providerId, date) {
    const params = {
      date,
    };

    try {
      const res = await this.axios.get(
        `providers/${providerId}/orders/report`, { params, baseURL: baseURLV2 }
      );
      return res.data;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} providerID
   * @returns {Promise}
   */
  async getProviderSms(providerID) {
    try {
      const res = await this.axios.get(`providers/${providerID}/sms`);
      return res.data;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @returns {Promise}
   */
  async getEmployees(providerId) {
    try {
      const res = await this.axios.get(`providers/${providerId}/employees`);
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @param {number} providerId
   * @param {number} phone
   * @returns {Promise}
   */
  async addEmployee(providerId, phone) {
    try {
      /**
       * Admin
       */
      if (authService.privileged) {
        return await adminApiService.addEmployee(providerId, phone);
      }

      const res = await this.axios.post(
        `providers/${providerId}/users/${convertToEnglishDigit(
          phone
        )}/employees`
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @param {number} providerId
   * @param {number} phone
   * @returns {Promise}
   */
  async deleteEmployee(providerId, phone) {
    try {
      const res = await this.axios.delete(
        `providers/${providerId}/users/${convertToEnglishDigit(
          phone
        )}/employees`
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @returns {Promise}
   */
  async getAllAbilities() {
    try {
      const res = await this.axios.get(`abilities`, { baseURL: baseURLV2 });
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @returns {Promise}
   */
  async getEmployeeAbilities(providerId, userId) {
    try {
      const res = await this.axios.get(
        `providers/${providerId}/employees/${userId}/abilities`, { baseURL: baseURLV2 }
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @returns {Promise}
   */
  async addAbilities(providerId, employeeId, abilityIds) {
    try {
      const res = await this.axios.post(
        `providers/${providerId}/employees/${employeeId}/abilities`,
        { ids: abilityIds },
        { baseURL: baseURLV2 }
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @param {number} providerId
   * @param {string} message
   * @param {phone} string
   * @returns {Promise}
   */
  async sendSms(providerId, message, phone) {
    try {
      const res = await this.axios.post(`providers/${providerId}/sms`, {
        phones: [convertToEnglishDigit(phone)],
        message,
      });
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @returns {Promise}
   */
  async getAllMapPolygons() {
    try {
      const res = await this.axios.get(`providers/zones`);
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @param {number} providerId
   * @returns {Promise}
   */
  async getProviderDeliveryFees(providerId) {
    try {
      const res = await this.axios.get(`providers/${providerId}/delivery-fees`);
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * Save or Update the delivery fees
   *
   * @param {number} providerId
   * @param {number} zoneId Id of the zone on the map
   * @param {number} feeId  Id of the delivery fee row
   * @param {number} cost The delivery cost (delivery fee)
   * @param {boolean} isUpdate Flag to check is update or not
   * @returns {Promise}
   */
  async storeOrUpdateProviderPolygon(
    providerId,
    zondId,
    feeId,
    cost,
    isUpdate
  ) {
    const formData = new FormData();
    formData.append("fee", +cost);

    let url = null;
    if (isUpdate) {
      formData.append("_method", "PATCH");
      url = `providers/${providerId}/zones/${zondId}/delivery-fees/${feeId}`;
    } else {
      url = `providers/${providerId}/zones/${zondId}/delivery-fees`;
    }

    try {
      const res = await this.axios.post(url, formData);
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * Delete the delivery fee
   *
   * @param {number} providerId
   * @param {number} zoneId Id of the zone on the map
   * @param {number} feeId  Id of the delivery fee row
   * @returns {Promise}
   */
  async deleteDeliveryFee(providerId, zoneId, feeId) {
    try {
      const res = await this.axios.delete(
        `providers/${providerId}/zones/${zoneId}/delivery-fees/${feeId}`
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * Delete the delivery fee
   *
   * @param {number} providerId
   * @param {number} lt Latitude
   * @param {number} lg Longitude
   * @param {string} location  Address detail
   * @param {number} addressId  Address id
   * @param {number} isUpdate  Flag to check is update or not
   * @returns {Promise}
   */
  async submitOrUpdateAddress(
    providerId,
    lt,
    lg,
    location,
    addressId,
    isUpdate
  ) {
    const formData = new FormData();
    formData.append("lt", lt);
    formData.append("lg", lg);
    formData.append("location", location);

    let url = `providers/${providerId}/addresses`;
    if (isUpdate) {
      url = `providers/${providerId}/addresses/${addressId}`;
      formData.append("_method", "PATCH");
    }

    try {
      const res = await this.axios.post(url, formData);
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * Delete the provider address
   *
   * @param {number} providerId
   * @param {number} addressId
   * @returns {Promise}
   */
  async deleteAddress(providerId, addressId) {
    try {
      const res = await this.axios.delete(
        `providers/${providerId}/addresses/${addressId}`
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @param {number} providerId
   * @returns {Promise}
   */
  async getProviderNotes(providerId) {
    try {
      const res = await this.axios.get(`providers/${providerId}/notes`);
      return res.data;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @param {number} providerId
   * @returns {Promise}
   */
  async submitProviderNotes(providerId, notes, noteId) {
    const formData = new FormData();
    formData.append("content", JSON.stringify(JSON.stringify(notes)));

    let url = `providers/${providerId}/notes`;
    if (noteId) {
      url = `providers/${providerId}/notes/${noteId}`;
      formData.append("_method", "PATCH");
    }

    try {
      const res = await this.axios.post(url, formData);
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @param {number} providerId
   * @param {any[]} hours
   * @param {number} hourId
   * @returns {Promise}
   */
  async submitProviderHours(providerId, hours, hourId) {
    const formData = new FormData();
    formData.append("details", JSON.stringify(JSON.stringify(hours)));

    let url = `providers/${providerId}/hours`;
    if (hourId) {
      url = `providers/${providerId}/hours/${hourId}`;
      formData.append("_method", "PATCH");
    }

    try {
      const res = await this.axios.post(url, formData);
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @param {number} providerId
   * @returns {Promise}
   */
  async getProviderHours(providerId) {
    try {
      const res = await this.axios.get(`providers/${providerId}/hours`);
      return res.data;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @param {number} providerId
   * @returns {Promise}
   */
  async getProviderCoupons(providerId, page) {
    try {
      const res = await this.axios.get(
        `providers/${providerId}/coupons?page=${page}`
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @param {number} providerId
   * @param {string} code coupon name
   * @param {string} value coupon value (percentage or amout)
   * @param {boolean} percentage_based is based on percentage or not
   * @param {string} expires_at coupon expiration date
   * @param {number} couponId coupon id
   * @returns {Promise}
   */
  async createOrEditCoupon(
    providerId,
    { code, value, percentage_based, expires_at, unlimited },
    couponId
  ) {
    const formData = new FormData();

    formData.append("code", convertToEnglishDigit(code));
    formData.append("value", value);
    formData.append("percentage_based", percentage_based ? 1 : 0);
    formData.append("expires_at", expires_at);
    formData.append('unlimited', unlimited ? 1 : 0);

    let url = `providers/${providerId}/coupons`;
    if (couponId) {
      formData.append("_method", "PATCH");
      url = `providers/${providerId}/coupons/${couponId}`;
    }

    try {
      const res = await this.axios.post(url, formData);
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @param {number} providerId
   * @param {number} couponId
   * @returns {Promise}
   */
  async deleteCoupon(providerId, couponId) {
    try {
      const res = await this.axios.delete(
        `providers/${providerId}/coupons/${couponId}`
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @returns {Promise}
   */
  async latestRelease() {
    try {
      const res = await this.axios.get(
        `users/releases/latest?application=business_windows_dashboard`
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @param {number} providerId
   * @param {number} page
   * @returns {Promise}
   */
  async getProviderTickets(providerId, page) {
    try {
      const res = await this.axios.get(
        `providers/${providerId}/tickets?page=${page}`,
        { baseURL: baseURLV2 }
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @param {number} providerId
   * @param {number} ticketId
   * @returns {Promise}
   */
  async getTicketDetails(providerId, ticketId) {
    try {
      const res = await this.axios.get(
        `providers/${providerId}/tickets/${ticketId}`,
        { baseURL: baseURLV2 }
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * Create or edit ticket
   *
   * @returns {Promise}
   */
  async postCreateOrEditTicket({
    id,
    title,
    content,
    department,
    priority,
    status,
    attachments,
    providerId,
  }) {
    try {
      const formData = new FormData();

      formData.append("user_id", authService.id.toString());
      if (title) formData.append("title", title);
      if (content) formData.append("content", content);
      if (department) formData.append("department", department);
      if (priority) formData.append("priority", priority);
      if (status) formData.append("status", status);

      if (attachments?.length > 0) {
        for (const item of attachments) {
          if (item instanceof File) {
            formData.append("attachments[]", item, generateFileName(item));
          }
        }
      }

      let url = `providers/${providerId}/tickets`;
      if (id) {
        url = url + `/${id}`;
        formData.append("_method", "PATCH");
      }

      const result = await this.axios.post(url, formData, {
        baseURL: baseURLV2,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return result;
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * Reply ticket
   *
   * @returns {Promise}
   */
  async postReplyTicket({ id, content, attachments, providerId }) {
    try {
      const formData = new FormData();

      formData.append("user_id", authService.id.toString());
      if (content) formData.append("content", content);

      if (attachments?.length > 0) {
        for (const item of attachments) {
          if (item instanceof File) {
            formData.append("attachments[]", item, generateFileName(item));
          }
        }
      }

      const result = await this.axios.post(
        `providers/${providerId}/tickets/${id}/reply`,
        formData,
        {
          baseURL: baseURLV2,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return result;
    } catch (e) {
      console.log(e);
    }
  }

  /**
 * 
 * @param {number} providerId 
 * @param {number} attachmentId 
 * @returns 
 */
  async deleteAttachment(middleUrl, providerId, attachmentId) {
    const providerURL = `providers/${providerId}/`;
    const mediaURL = `media/${attachmentId}`;

    let url = providerURL + mediaURL;

    if (middleUrl) {
      url = providerURL + middleUrl + mediaURL;
    }

    try {
      const res = await this.axios.delete(
        url,
        // `providers/${providerId}/media/${attachmentId}` // Logo - Cover
        // `providers/${providerId}/category/${id}/media/${attachmentId}` // Category
        // `providers/${providerId}/category/${id}/item/${id}/media/${attachmentId}` // Product
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @param {string} phone
   * @returns {Promise}
   */
  async searchUser(providerId, phone) {
    try {
      const res = await this.axios.get(
        `/users/search?value=${phone}&provider_id=${providerId}`,
        { baseURL: baseURLV2 }
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @returns {Promise}
   */
  async registerUser(providerId, user) {
    const formData = new FormData();

    formData.append('provider_id', providerId);
    formData.append('name', user.name);

    let url = `/users`;
    if (user.id) {
      formData.append('_method', 'PATCH');
      url += `/${user.id}`;
    } else {
      formData.append('phone', convertToEnglishDigit(user.phone));
    }

    try {
      const res = await this.axios.post(url, formData, { baseURL: baseURLV2 });
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @returns {Promise}
   */
  async addCustomer(providerId, userId) {
    const formData = new FormData();

    try {
      const res = await this.axios.post(`/providers/${providerId}/customers/${userId}`, formData, { baseURL: baseURLV2 });
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @returns {Promise}
   */
  async getProviderTablesList(providerId) {
    try {
      const res = await this.axios.get(`/providers/${providerId}/tables`, { baseURL: baseURLV2 });
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @returns {Promise}
   */
  async createTable(props) {
    const formData = new FormData();

    formData.append('title', props.title);
    if (props.capacity) formData.append('capacity', props.capacity);
    if (props.isOutside) formData.append('is_outside', props.isOutside ? 1 : 0);
    if (props.isAvailable) formData.append('is_available', props.isAvailable ? 1 : 0);
    if (props.isOccupied) formData.append('is_occupied', props.isOccupied ? 1 : 0);
    if (props.isReserved) formData.append('is_reserved', props.isReserved ? 1 : 0);

    let url = `/providers/${props.providerId}/tables`;
    if (props.tableId) {
      url += `/${props.tableId}`;
      formData.append('_method', 'PATCH');
    }

    try {
      const res = await this.axios.post(url, formData, { baseURL: baseURLV2 });
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   * @returns {Promise}
   */
  async deteleTable(providerId, tableId) {
    try {
      const res = await this.axios.delete(`/providers/${providerId}/tables/${tableId}`, { baseURL: baseURLV2 });
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  async themes() {
    try {
      const res = await this.axios.get(`themes`, { baseURL: baseURLV2 });
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

}

const providerApiService = new ProviderApiService();
export default providerApiService;
